import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import heroImage from '@/assets/hero-pharmacist.webp';

const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-[82vh] sm:min-h-[75vh] md:min-h-[65vh] lg:min-h-[60vh] flex items-end sm:items-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-[center_35%] sm:bg-[center_25%]"
        style={{
          backgroundImage: `url(${heroImage})`,
        }}
      />
      
      {/* Darker Gradient Overlays for Premium Contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-foreground/50 to-foreground/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 via-foreground/20 to-foreground/10" />

      <div className="container relative z-10">
        <div className="max-w-3xl pt-24 pb-[44px] sm:pt-28 sm:pb-16 md:pt-32 md:pb-14 lg:pt-36 lg:pb-16">

          {/* Main Headline - Elegant editorial style */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal mb-4 sm:mb-6 leading-[1.12] tracking-[-0.01em] text-white"
          >
            Maßgeschneiderte
            <br />
            <span className="mt-1 sm:mt-2 block italic">
              Finanzrezepturen.
            </span>
          </motion.h1>

          {/* Subheadline - Refined, understated */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-base sm:text-lg md:text-xl text-white/70 max-w-xl mb-6 sm:mb-10 leading-relaxed font-light tracking-wide"
          >
            Unabhängige Beratung, spezialisiert auf Apotheker & Pharmaziestudierende.
          </motion.p>

          {/* CTA Buttons - Clear hierarchy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row items-start gap-3 sm:gap-5"
          >
            {/* Primary CTA - Refined */}
            <Link to="/beratung/individuell">
              <Button 
                size="lg" 
                className="bg-white text-foreground hover:bg-white/95 font-medium text-sm px-6 py-2.5 sm:px-8 sm:py-3 h-auto group shadow-md hover:shadow-lg transition-all duration-500 tracking-wide"
              >
                Beratung buchen
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            {/* Secondary CTA - Understated */}
            <Link to="/ueber-uns">
              <Button 
                variant="ghost" 
                size="lg" 
                className="text-white/85 hover:text-white hover:bg-white/5 font-normal text-sm px-4 sm:px-6 py-2.5 sm:py-3 h-auto tracking-wide"
              >
                Mehr erfahren
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="hidden md:block absolute bottom-12 left-1/2 -translate-x-1/2"
      >
        <div className="w-5 h-9 rounded-full border border-white/25 flex items-start justify-center p-1.5">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1 h-1.5 bg-white/60 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
